export function toMerchantString(value, fallback) {
  if (value === null || value === undefined) {
    return typeof fallback === "undefined" ? "" : fallback;
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number") {
    return String(value);
  }

  if (typeof value.toString === "function") {
    return value.toString();
  }

  return typeof fallback === "undefined" ? "" : fallback;
}

export function toMerchantNumber(value) {
  const normalized = toMerchantString(value, "0").replace(/,/g, "");
  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : 0;
}

function formatMerchantIntegerPart(value) {
  const normalizedInteger = toMerchantString(value, "0").replace(/^0+(?=\d)/, "");

  return normalizedInteger.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function formatMerchantTruncatedNumber(value, digits, fallback) {
  const fractionDigits = typeof digits === "number" ? Math.max(0, digits) : 2;
  const normalized = toMerchantString(value, "").trim().replace(/,/g, "");

  if (!normalized) {
    return typeof fallback === "undefined" ? "" : fallback;
  }

  const matched = normalized.match(/^([+-]?)(\d+)(?:\.(\d+))?$/);

  if (matched) {
    const sign = matched[1] === "-" ? "-" : "";
    const integerPart = formatMerchantIntegerPart(matched[2] || "0");
    const decimalPart = (matched[3] || "")
      .slice(0, fractionDigits)
      .padEnd(fractionDigits, "0");

    return fractionDigits > 0
      ? sign + integerPart + "." + decimalPart
      : sign + integerPart;
  }

  const parsed = Number(normalized);

  if (!Number.isFinite(parsed)) {
    return typeof fallback === "undefined" ? "" : fallback;
  }

  const factor = Math.pow(10, fractionDigits);
  const truncated =
    parsed < 0 ? Math.ceil(parsed * factor) / factor : Math.floor(parsed * factor) / factor;
  const absoluteFixed = Math.abs(truncated).toFixed(fractionDigits);
  const fixedMatch = absoluteFixed.match(/^(\d+)(?:\.(\d+))?$/);

  if (!fixedMatch) {
    return typeof fallback === "undefined" ? "" : fallback;
  }

  const integerPart = formatMerchantIntegerPart(fixedMatch[1] || "0");
  const decimalPart = (fixedMatch[2] || "")
    .slice(0, fractionDigits)
    .padEnd(fractionDigits, "0");
  const sign = truncated < 0 ? "-" : "";

  return fractionDigits > 0
    ? sign + integerPart + "." + decimalPart
    : sign + integerPart;
}

export function formatMerchantPercent(value, digits, fallback) {
  const formattedValue = formatMerchantTruncatedNumber(value, digits, fallback);

  if (!formattedValue) {
    return typeof fallback === "undefined" ? "" : fallback;
  }

  return formattedValue + "%";
}

export function pickMerchantField(record, keys, fallback) {
  if (!record) {
    return typeof fallback === "undefined" ? "" : fallback;
  }

  for (let index = 0; index < keys.length; index += 1) {
    const value = record[keys[index]];

    if (value !== null && value !== undefined && value !== "") {
      return value;
    }
  }

  return typeof fallback === "undefined" ? "" : fallback;
}

export function getMerchantDateKey(value) {
  const normalized = toMerchantString(value);

  return normalized ? normalized.slice(0, 10) : "";
}

export function getMerchantStatus(record) {
  const refundStatus = toMerchantString(
    pickMerchantField(record, ["refundStatus", "refund_status"], ""),
  ).toLowerCase();

  if (refundStatus && refundStatus !== "none" && refundStatus !== "null") {
    return refundStatus;
  }

  return toMerchantString(
    pickMerchantField(record, ["paymentStatus", "payment_status", "status"], "completed"),
  ).toLowerCase();
}

export function getMerchantStatusLabel(status) {
  const normalized = toMerchantString(status).toLowerCase();
  const labelMap = {
    success: "Completed",
    completed: "Completed",
    refunded: "Refunded",
    partial_refund: "Partial Refund",
    entire_refund: "Entire Refund",
    cancel: "Canceled",
    cancelled: "Canceled",
    fail: "Failed",
    failed: "Failed",
    pending: "Pending",
  };

  return labelMap[normalized] || normalized || "Unknown";
}

export function formatMerchantPaymentMethod(value, fallback) {
  const rawValue = toMerchantString(value, "").trim();

  if (!rawValue) {
    return typeof fallback === "undefined" ? "Payment" : fallback;
  }

  const normalizedKey = rawValue
    .toLowerCase()
    .replace(/\+/g, " plus ")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, "_");

  const labelMap = {
    dcs_card_now_pay: "DCS PAY",
    ali_merchant_scan_user_qr_pay: "ALI PAY",
    wechat_merchant_scan_user_qr_pay: "WECHAT PAY",
    dbs_pay_now_merchant_presented_qr_pay: "DBS PAY",
    card: "Payment Card",
    payment_card: "Payment Card",
    bank_card: "Payment Card",
    credit_card: "Payment Card",
    debit_card: "Payment Card",
    credit_debit_card: "Payment Card",
    card_payment: "Payment Card",
    wechat: "WeChat Pay",
    wechat_pay: "WeChat Pay",
    weixin: "WeChat Pay",
    weixin_pay: "WeChat Pay",
    alipay: "Alipay",
    alipay_plus: "Alipay+",
    unionpay: "UnionPay",
    union_pay: "UnionPay",
    unionpay_qr: "UnionPay",
    union_pay_qr: "UnionPay",
    grabpay: "GrabPay",
    grab_pay: "GrabPay",
    paynow: "PayNow",
    pay_now: "PayNow",
    apple_pay: "Apple Pay",
    applepay: "Apple Pay",
    google_pay: "Google Pay",
    googlepay: "Google Pay",
    gpay: "Google Pay",
    shopback_paylater: "ShopBack PayLater",
    shopback_pay_later: "ShopBack PayLater",
    atome: "Atome",
    cash: "Cash",
  };

  if (labelMap[normalizedKey]) {
    return labelMap[normalizedKey];
  }

  return normalizedKey
    .split("_")
    .filter(Boolean)
    .map(function formatWord(word) {
      if (word === "qr") {
        return "QR";
      }

      if (word === "pos") {
        return "POS";
      }

      if (word === "api") {
        return "API";
      }

      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

export function getMerchantMethod(record) {
  return formatMerchantPaymentMethod(
    pickMerchantField(record, [
      "dcsPaymentMethod",
      "dcs_payment_method",
      "paymentMethod",
      "payment_method",
      "acquirerTypeString",
      "transactionSource",
    ]),
    "Payment",
  );
}

export function getMerchantStoreName(record) {
  return toMerchantString(
    pickMerchantField(record, ["storeName", "store_name", "merchantName", "merchant_name", "name"]),
    "Unknown Store",
  );
}

export function getMerchantCountryCode(record) {
  const normalizedMethod = toMerchantString(getMerchantMethod(record), "")
    .toLowerCase()
    .trim();

  if (
    normalizedMethod === "wechat pay" ||
    normalizedMethod === "alipay" ||
    normalizedMethod === "alipay+"
  ) {
    return "CN";
  }

  return toMerchantString(
    pickMerchantField(record, ["cardBelong", "card_belong", "cardIssuerCode", "issuerCountry", "issuer_country"]),
    "UNKNOWN",
  ).toUpperCase();
}

export function getMerchantSessionBusinessId(user) {
  const directBusinessId = pickMerchantField(
    user,
    ["businessId", "companyId", "merchantBusinessId"],
    "",
  );

  if (directBusinessId !== "") {
    return toMerchantString(directBusinessId);
  }

  const businessIdList = Array.isArray(user && user.businessIdList)
    ? user.businessIdList
    : [];

  if (businessIdList.length > 0) {
    if (
      businessIdList[0] &&
      typeof businessIdList[0] === "object" &&
      businessIdList[0].id !== undefined
    ) {
      return toMerchantString(businessIdList[0].id);
    }

    return toMerchantString(businessIdList[0]);
  }

  return "";
}

export function getMerchantAuthBusinessId(user) {
  return getMerchantSessionBusinessId(user);
}

export function getMerchantSessionBusinessIds(user) {
  if (Array.isArray(user && user.businessIdList)) {
    return user.businessIdList
      .map(function mapBusinessId(businessId) {
        if (businessId && typeof businessId === "object" && businessId.id !== undefined) {
          return toMerchantString(businessId.id);
        }

        return toMerchantString(businessId);
      })
      .filter(Boolean);
  }

  const directBusinessId = getMerchantSessionBusinessId(user);

  if (directBusinessId) {
    return [directBusinessId];
  }

  return [];
}

export function getMerchantDisplayName(user, fallback) {
  const displayName = [
    pickMerchantField(user, ["displayName", "display_name", "firstName"]),
    pickMerchantField(user, ["lastName"]),
  ]
    .filter(Boolean)
    .join(" ");

  if (displayName) {
    return displayName;
  }

  return toMerchantString(
    pickMerchantField(user, ["name", "username", "emailAddress", "email"]),
    fallback || "Merchant User",
  );
}

export function toMerchantDateTimeStart(value) {
  const normalized = toMerchantString(value);

  return normalized ? normalized + " 00:00:00" : "";
}

export function toMerchantDateTimeEnd(value) {
  const normalized = toMerchantString(value);

  return normalized ? normalized + " 23:59:59" : "";
}
